import { Link } from 'react-router-dom'
import { Mail, Phone, MapPin, Linkedin, Facebook, Clock } from 'lucide-react'
import { COMPANY, NAV_ITEMS } from '@/lib/constants'

export default function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-primary-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <h3 className="text-xl font-bold mb-4">{COMPANY.name}</h3>
            <p className="text-primary-200 mb-6 text-sm leading-relaxed">
              Professional accounting, bookkeeping, and tax services for
              businesses and individuals in Edmonton, Alberta.
            </p>
            <div className="flex gap-4">
              <a
                href={COMPANY.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-primary-800 rounded-lg hover:bg-primary-700 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href={COMPANY.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-primary-800 rounded-lg hover:bg-primary-700 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {NAV_ITEMS.filter((item) => !item.children).map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-primary-200 hover:text-white text-sm transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/faq"
                  className="text-primary-200 hover:text-white text-sm transition-colors"
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link
                  to="/team"
                  className="text-primary-200 hover:text-white text-sm transition-colors"
                >
                  Our Team
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-4">Services</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/services/bookkeeping"
                  className="text-primary-200 hover:text-white text-sm transition-colors"
                >
                  Bookkeeping
                </Link>
              </li>
              <li>
                <Link
                  to="/services/payroll"
                  className="text-primary-200 hover:text-white text-sm transition-colors"
                >
                  Payroll Services
                </Link>
              </li>
              <li>
                <Link
                  to="/services/tax-preparation"
                  className="text-primary-200 hover:text-white text-sm transition-colors"
                >
                  Tax Preparation
                </Link>
              </li>
              <li>
                <Link
                  to="/services/financial-consulting"
                  className="text-primary-200 hover:text-white text-sm transition-colors"
                >
                  Financial Consulting
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="text-accent-400 hover:text-accent-300 text-sm transition-colors font-medium"
                >
                  View All Services
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`tel:${COMPANY.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-start gap-3 text-primary-200 hover:text-white text-sm transition-colors"
                >
                  <Phone className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {COMPANY.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-start gap-3 text-primary-200 hover:text-white text-sm transition-colors"
                >
                  <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-primary-200 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  {COMPANY.address.street}
                  <br />
                  {COMPANY.address.city}, {COMPANY.address.province}{' '}
                  {COMPANY.address.postalCode}
                </span>
              </li>
              <li className="flex items-start gap-3 text-primary-200 text-sm">
                <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  Mon-Fri: {COMPANY.hours.weekdays}
                  <br />
                  Sat: {COMPANY.hours.saturday}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-primary-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-primary-300 text-sm">
              &copy; {currentYear} {COMPANY.name}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy"
                className="text-primary-300 hover:text-white text-sm transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-primary-300 hover:text-white text-sm transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
